import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ShieldCheck, ShieldAlert, Award, Calendar, User,
  GraduationCap, Hash, CheckCircle, XCircle, ArrowLeft, Download,
  Share2, Copy, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CertificateVerifyPage() {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharing, setSharing] = useState(false);

  const downloadPdf = () => {
    window.open(`${API_URL}/api/platform/certificates/pdf/${certId}/`, '_blank');
  };

  const downloadStoryCard = async () => {
    setSharing(true);
    try {
      const res = await axios.get(`${API_URL}/api/platform/certificates/story/${certId}/`);
      const { image_base64, filename } = res.data;
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${image_base64}`;
      link.download = filename;
      link.click();
      toast.success('Story card saved! Share it on TikTok, Instagram, or Snapchat!');
    } catch {
      toast.error('Failed to generate story card');
    } finally {
      setSharing(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Verification link copied!');
  };

  useEffect(() => {
    const fetchCert = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/platform/certificates/verify/${certId}/`);
        setCert(res.data);
      } catch (e) {
        if (e.response?.status === 404) {
          setError('Certificate not found. Please check the certificate ID and try again.');
        } else {
          setError('Unable to verify certificate at this time. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    if (certId) fetchCert();
  }, [certId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center" data-testid="cert-verify-loading">
        <div className="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center p-4" data-testid="cert-verify-error">
        <Card className="max-w-lg w-full border-red-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Verification Failed</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <p className="text-sm text-gray-400 mb-4">Certificate ID: {certId}</p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isValid = cert?.verification_result?.valid;
  const hashVerified = cert?.verification_result?.hash_verified;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 py-12 px-4" data-testid="cert-verify-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:underline mb-4">
            <ArrowLeft className="h-4 w-4" /> Back to Ready Set STEM
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Certificate Verification</h1>
          <p className="text-gray-500 text-sm mt-1">Blockchain-verified credential</p>
        </div>

        {/* Status Banner */}
        <div className={`rounded-xl p-4 mb-6 flex items-center gap-3 ${
          isValid ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'
        }`} data-testid="cert-verify-status">
          {isValid ? (
            <>
              <ShieldCheck className="h-6 w-6 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-emerald-800">Verified & Authentic</p>
                <p className="text-sm text-emerald-600">This certificate is valid and has not been tampered with.</p>
              </div>
            </>
          ) : (
            <>
              <ShieldAlert className="h-6 w-6 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-800">Verification Warning</p>
                <p className="text-sm text-amber-600">This certificate may have been modified or revoked.</p>
              </div>
            </>
          )}
        </div>

        {/* Certificate Card */}
        <Card className="border-2 border-indigo-100 overflow-hidden" data-testid="cert-verify-card">
          {/* Top accent */}
          <div className="h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500" />
          
          <CardContent className="p-8">
            {/* Logo + Title */}
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center mx-auto mb-3">
                <Award className="h-7 w-7 text-indigo-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Ready Set STEM</h2>
              <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">Certificate of Achievement</p>
            </div>

            {/* Certificate Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid="cert-holder-name">
                <User className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 uppercase">Holder</p>
                  <p className="font-semibold text-gray-900">{cert.holder_name}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid="cert-track">
                  <GraduationCap className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase">Track</p>
                    <p className="font-semibold text-gray-900 text-sm">{cert.track}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid="cert-grade">
                  <Award className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase">Grade</p>
                    <p className="font-bold text-2xl text-indigo-600">{cert.grade}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid="cert-score">
                  <Hash className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase">Score</p>
                    <p className="font-semibold text-gray-900">{cert.score}%</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid="cert-date">
                  <Calendar className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase">Issued</p>
                    <p className="font-semibold text-gray-900 text-sm">
                      {new Date(cert.issued_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Issuer */}
              <div className="p-3 bg-indigo-50 rounded-lg text-center" data-testid="cert-issuer">
                <p className="text-xs text-indigo-400 uppercase">Issued by</p>
                <p className="font-semibold text-indigo-700">{cert.issuer_name}</p>
                <p className="text-xs text-indigo-500">{cert.issuer_title}</p>
              </div>
            </div>

            {/* Verification Details */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Verification Details</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Certificate ID</span>
                  <span className="font-mono text-xs text-gray-700" data-testid="cert-id-display">{cert.cert_id}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Hash Integrity</span>
                  <div className="flex items-center gap-1">
                    {hashVerified ? (
                      <><CheckCircle className="h-4 w-4 text-emerald-500" /><span className="text-emerald-600 font-medium">Verified</span></>
                    ) : (
                      <><XCircle className="h-4 w-4 text-red-500" /><span className="text-red-600 font-medium">Failed</span></>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Status</span>
                  <Badge variant={cert.status === 'active' ? 'default' : 'destructive'} className={cert.status === 'active' ? 'bg-emerald-100 text-emerald-700' : ''}>
                    {cert.status}
                  </Badge>
                </div>
                <div className="mt-3 p-2 bg-gray-50 rounded text-[10px] font-mono text-gray-400 break-all" data-testid="cert-hash">
                  SHA-256: {cert.sha256_hash}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-4" data-testid="cert-actions">
          <Button onClick={downloadPdf} variant="outline" className="flex-1" data-testid="cert-download-pdf">
            <Download className="h-4 w-4 mr-2" /> Download PDF
          </Button>
          <Button onClick={downloadStoryCard} disabled={sharing}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white"
                  data-testid="cert-share-story">
            {sharing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Share2 className="h-4 w-4 mr-2" />}
            Share to Stories
          </Button>
          <Button onClick={copyLink} variant="outline" data-testid="cert-copy-link">
            <Copy className="h-4 w-4" />
          </Button>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Verified at {new Date().toLocaleString()} | Ready Set STEM Platform
        </p>
      </div>
    </div>
  );
}
